import type { ReactNode } from 'react';
import { GraduationCap } from 'lucide-react';
import { env } from '@/config/env';
import { cn } from '@/lib/utils';

export interface LogoProps {
  /** `light` for the dark panel, `dark` for the paper background. */
  tone?: 'light' | 'dark';
  showText?: boolean;
  /**
   * The school's name. A signed-in panel passes its own institute's, so a child
   * at one school is never shown another's. Empty while it loads.
   */
  title?: string;
  subtitle?: string;
  /**
   * The school's own logo, drawn on a white tile in place of the amber mark.
   * Omitted — no logo uploaded, or still loading — the amber mark shows.
   */
  mark?: ReactNode;
  className?: string;
}

/**
 * The amber mark from the design mock. Swap the icon for the institute's real
 * crest by replacing <GraduationCap /> with an <img>.
 */
export function Logo({ tone = 'dark', showText = true, title, subtitle, mark, className }: LogoProps) {
  return (
    <div className={cn('flex items-center gap-2.5 min-w-0', className)}>
      {mark ? (
        <span
          className="grid h-[30px] w-[30px] shrink-0 place-items-center overflow-hidden rounded-[8px] bg-white"
          aria-hidden
        >
          {mark}
        </span>
      ) : (
        <span
          className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[8px] bg-amber text-[#3A2A02]"
          aria-hidden
        >
          <GraduationCap className="h-[17px] w-[17px]" strokeWidth={1.9} />
        </span>
      )}

      {showText && (
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-sm sm:text-base font-[650] leading-tight',
              tone === 'light' ? 'text-[#E7EEEB]' : 'text-ink',
            )}
          >
            {/* The product's own name, from config, when no school is named —
                never one school's name baked into the build (CLAUDE.md §4). */}
            {title ?? env.appName}
          </span>
          {subtitle && (
            <span
              className={cn('block truncate text-2xs sm:text-xs', tone === 'light' ? 'text-[#8FA5A0]' : 'text-ink-2')}
            >
              {subtitle}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
